import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';

export interface UserAddress {
  id: string;
  name: string; // e.g. "Home", "Work", "Aqua Tower", etc.
  address: string; // e.g. "House 12, Road 4, Mirpur"
  city: string; // e.g. "Dhaka"
  latitude: number;
  longitude: number;
  isCurrent?: boolean;
  tag?: 'home' | 'work' | 'other';
  createdAt: number;
}

const SAVED_ADDRESSES_KEY = '@salunbd/saved-addresses-v2';
const ACTIVE_ADDRESS_KEY = '@salunbd/active-address-v2';

// Known IDs of old static placeholder demo addresses to automatically purge
const LEGACY_DUMMY_IDS = new Set([
  'addr-aqua-tower',
  'addr-darussalam-22',
  'addr-salem-uddin',
  'addr-darussalam-main',
  'addr-mirpur-road',
]);

/**
 * Filter out any legacy dummy / static seeded addresses from a list
 */
function filterOutDummyAddresses(list: any[]): UserAddress[] {
  if (!Array.isArray(list)) return [];
  return list.filter((item): item is UserAddress => {
    if (!item || typeof item !== 'object') return false;
    if (LEGACY_DUMMY_IDS.has(item.id)) return false;
    return Boolean(item.id && item.name && typeof item.latitude === 'number');
  });
}

/**
 * Retrieve all saved addresses from AsyncStorage (returns ONLY user-added addresses)
 */
export async function getSavedAddresses(): Promise<UserAddress[]> {
  try {
    const raw = await AsyncStorage.getItem(SAVED_ADDRESSES_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    const cleaned = filterOutDummyAddresses(parsed);

    // If dummy data was present, scrub it from storage now
    if (cleaned.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
      await AsyncStorage.setItem(SAVED_ADDRESSES_KEY, JSON.stringify(cleaned));
    }
    return cleaned;
  } catch {
    return [];
  }
}

/**
 * Save a newly added address to storage (supports optional preexisting ID from backend)
 */
export async function saveAddress(
  address: (Omit<UserAddress, 'id' | 'createdAt'> & { id?: string; createdAt?: number }) | UserAddress,
): Promise<UserAddress> {
  const all = await getSavedAddresses();
  const newAddr: UserAddress = {
    name: address.name,
    address: address.address,
    city: address.city || 'Dhaka',
    latitude: address.latitude,
    longitude: address.longitude,
    tag: address.tag || 'home',
    isCurrent: Boolean(address.isCurrent),
    id: address.id || `addr-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    createdAt: address.createdAt || Date.now(),
  };

  const updated = [newAddr, ...all.filter((a) => a.id !== newAddr.id)];
  await AsyncStorage.setItem(SAVED_ADDRESSES_KEY, JSON.stringify(updated));
  await setActiveAddress(newAddr);
  return newAddr;
}

/**
 * Update an existing address
 */
export async function updateAddress(
  id: string,
  updates: Partial<Omit<UserAddress, 'id' | 'createdAt'>>,
): Promise<UserAddress | null> {
  const all = await getSavedAddresses();
  const index = all.findIndex((a) => a.id === id);
  if (index === -1) return null;

  const updatedAddr: UserAddress = {
    ...all[index],
    ...updates,
  };

  all[index] = updatedAddr;
  await AsyncStorage.setItem(SAVED_ADDRESSES_KEY, JSON.stringify(all));

  // If the updated address is currently active, update active record too
  const active = await getActiveAddress();
  if (active && active.id === id) {
    await setActiveAddress(updatedAddr);
  }

  return updatedAddr;
}

/**
 * Delete a saved address
 */
export async function deleteAddress(id: string): Promise<void> {
  const all = await getSavedAddresses();
  const filtered = all.filter((a) => a.id !== id);
  await AsyncStorage.setItem(SAVED_ADDRESSES_KEY, JSON.stringify(filtered));

  const active = await getActiveAddress();
  if (active && active.id === id) {
    if (filtered.length > 0) {
      await setActiveAddress(filtered[0]);
    } else {
      await AsyncStorage.removeItem(ACTIVE_ADDRESS_KEY);
    }
  }
}

/**
 * Retrieve the currently selected active address
 */
/** Whether the app may use the phone's location right now. */
async function locationAllowed(): Promise<boolean> {
  try {
    return (await Location.getForegroundPermissionsAsync()).granted;
  } catch {
    return false;
  }
}

export async function getActiveAddress(): Promise<UserAddress | null> {
  const active = await readActiveAddress();
  // "Use current location" came from the phone's GPS: with the permission off (or taken away later) it
  // is not used, so a place that was never allowed cannot stay selected. Addresses the customer typed or
  // picked on the map do not depend on the permission.
  if (active?.isCurrent && !(await locationAllowed())) return null;
  return active;
}

async function readActiveAddress(): Promise<UserAddress | null> {
  try {
    const raw = await AsyncStorage.getItem(ACTIVE_ADDRESS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as UserAddress;
      if (parsed && !LEGACY_DUMMY_IDS.has(parsed.id)) {
        return parsed;
      }
      // If it was a dummy address, purge it
      await AsyncStorage.removeItem(ACTIVE_ADDRESS_KEY);
    }
    const all = await getSavedAddresses();
    if (all.length > 0) {
      await setActiveAddress(all[0]);
      return all[0];
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Set the currently active address
 */
export async function setActiveAddress(address: UserAddress): Promise<void> {
  try {
    if (LEGACY_DUMMY_IDS.has(address.id)) return;
    await AsyncStorage.setItem(ACTIVE_ADDRESS_KEY, JSON.stringify(address));
  } catch {
    // Best effort
  }
}

/**
 * Forget every saved and active address on this phone. Called on sign-out: the keys are per
 * device, not per account, so the next person to sign in (or a guest) used to see the previous
 * account's home address.
 */
export async function clearSavedAddresses(): Promise<void> {
  try {
    await AsyncStorage.multiRemove([SAVED_ADDRESSES_KEY, ACTIVE_ADDRESS_KEY]);
  } catch {
    // Best effort
  }
}

/**
 * Generate a Mapbox static snapshot preview image URL for a given coordinate
 */
export function getMapPreviewUrl(lat: number, lng: number, width = 640, height = 260): string {
  const token = process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN;
  if (!token) return '';
  return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+e11d48(${lng.toFixed(5)},${lat.toFixed(5)})/${lng.toFixed(5)},${lat.toFixed(5)},15,0/${width}x${height}@2x?access_token=${token}`;
}

/**
 * Synchronize local cache when backend addresses are loaded
 */
export async function syncSavedAddressesFromBackend(backendAddresses: any[]): Promise<UserAddress[]> {
  if (!Array.isArray(backendAddresses)) {
    return getSavedAddresses();
  }
  const normalized: UserAddress[] = backendAddresses.map((a) => ({
    id: a.id,
    name: a.name,
    address: a.address,
    city: a.city || 'Dhaka',
    latitude: Number(a.latitude),
    longitude: Number(a.longitude),
    tag: a.tag || 'home',
    isCurrent: false,
    createdAt: typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : (a.createdAt || Date.now()),
  }));

  const cleaned = filterOutDummyAddresses(normalized);
  await AsyncStorage.setItem(SAVED_ADDRESSES_KEY, JSON.stringify(cleaned));
  return cleaned;
}
